import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ExternalLink, Heart } from "lucide-react";
import { TIMELINE, FOUNDERS, PRESS, EKTA } from "@/data/content";
import ArchDivider from "@/components/site/ArchDivider";
import { IAWordmark } from "@/components/site/Wordmarks";
import Seo from "@/components/site/Seo";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
};

export default function About() {
  return (
    <div className="bg-[#FDFBF6]">
      <Seo
        title="Our Story — Indian Alchemy × Platinum Hospitality | 25+ Years of Culinary Excellence"
        description="Three generations, one family kitchen in Udyog Vihar, Gurugram. Meet the founders behind Indian Alchemy luxury event catering and Platinum Hospitality corporate catering — 25+ years serving Delhi NCR."
        path="/about"
        keywords="Indian Alchemy founders, Platinum Hospitality Gurugram, catering company Udyog Vihar, family-run caterer Delhi NCR, 25 years catering experience"
      />
      {/* EDITORIAL HERO — full-width headline */}
      <section className="relative pt-32 md:pt-44 pb-8 md:pb-12">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#C9A961]">
            About · Founders · Legacy
          </div>
          <h1 className="mt-6 font-serif font-light text-[9vw] sm:text-[7vw] lg:text-[5.2vw] leading-[0.98] tracking-[-0.005em] text-[#3B1F1A]">
            A Legacy of Flavour.
            <br />
            <em className="italic text-[#C9A961]">A Future of Excellence.</em>
          </h1>
        </div>
      </section>

      {/* TWO-COLUMN STORY with big event photo BETWEEN */}
      <section className="relative pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start">
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-80px" }}
              className="lg:col-span-5"
            >
              <div className="font-mono text-[10px] tracking-widest uppercase text-[#C9A961]">
                Chapter I · Origin
              </div>
              <p className="mt-4 font-serif text-xl md:text-[1.4rem] leading-relaxed text-[#3B1F1A]">
                Yogesh N Saxena founded Platinum Hospitality in 2001. From one contract in Udyog Vihar, he built a reputation for reliable, scalable corporate catering — quietly earning the trust of some of Delhi NCR&rsquo;s biggest MNCs.
              </p>
            </motion.div>

            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-80px" }}
              className="lg:col-span-5 lg:col-start-8"
            >
              <div className="font-mono text-[10px] tracking-widest uppercase text-[#C9A961]">
                Chapter II · Alchemy
              </div>
              <p className="mt-4 font-serif text-xl md:text-[1.4rem] leading-relaxed text-[#3B1F1A]">
                The next generation — Atul Celly and Jhanvi Celly Saxena — grew up in that business. Jhanvi joined at sixteen. In 2018 they launched Indian Alchemy™: the same kitchen excellence, elevated into luxury event catering. Heritage meeting innovation. Tradition meeting ambition.
              </p>
            </motion.div>
          </div>
          <div className="mt-14 md:mt-16 flex justify-center text-[#C9A961]">
            <ArchDivider />
          </div>
        </div>
      </section>

      {/* IN MEMORIAM — Ekta Celly · warm gold tribute */}
      <section className="relative py-24 md:py-36 overflow-hidden">
        {/* soft gold radial + peach base */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#F5E1C4] via-[#FDE8DF] to-[#FDFBF6]" />
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(60% 55% at 50% 40%, rgba(201,169,97,0.35) 0%, rgba(201,169,97,0.08) 45%, transparent 75%)",
          }}
        />
        <div className="absolute inset-0 dot-grid opacity-30 pointer-events-none" />

        <div className="relative max-w-4xl mx-auto px-6 md:px-10 text-center">
          <div className="flex justify-center mb-8 text-[#C9A961]">
            <ArchDivider />
          </div>

          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.32em] text-[#C9A961]">
            <Heart size={13} className="fill-[#C9A961]" strokeWidth={0} />
            In Loving Memory
          </div>

          {/* Portrait — placeholder circular monogram until /ekta.jpg exists */}
          <div className="mt-10 flex justify-center">
            <div className="relative">
              <div
                aria-hidden
                className="absolute -inset-6 rounded-full"
                style={{
                  background:
                    "radial-gradient(closest-side, rgba(201,169,97,0.35), transparent 70%)",
                }}
              />
              <div className="relative h-40 w-40 md:h-52 md:w-52 rounded-full overflow-hidden ring-1 ring-[#C9A961]/60 ring-offset-8 ring-offset-[#FDE8DF] bg-[#FDFBF6] flex items-center justify-center">
                {EKTA.photo ? (
                  <img
                    src={EKTA.photo}
                    alt={EKTA.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="font-serif italic text-6xl md:text-7xl text-[#C9A961]">
                    EC
                  </span>
                )}
              </div>
            </div>
          </div>

          <h2 className="mt-10 font-serif font-light text-4xl md:text-6xl leading-[1.02] text-[#3B1F1A]">
            {EKTA.name}
          </h2>
          <div className="mt-3 font-serif italic text-xl md:text-2xl text-[#C9A961]">
            {EKTA.epitaph}
          </div>
          {EKTA.years && (
            <div className="mt-2 text-[11px] uppercase tracking-[0.32em] text-[#3B1F1A]/60">
              {EKTA.years}
            </div>
          )}

          <p className="mt-10 md:mt-12 font-serif text-lg md:text-xl leading-relaxed text-[#3B1F1A]/90 max-w-2xl mx-auto">
            {EKTA.body}
          </p>

          <div className="mt-12 flex justify-center text-[#C9A961]">
            <ArchDivider flip />
          </div>
        </div>
      </section>

      {/* FOUNDERS — Atul + Jhanvi active, Yogesh as legacy mention */}
      <section className="relative bg-[#F5C9B0]/20 py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="mb-16 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <div className="text-[11px] uppercase tracking-[0.3em] text-[#C9A961]">
                Founders
              </div>
              <h2 className="mt-6 font-serif font-light text-4xl md:text-5xl leading-[1.02] text-[#3B1F1A]">
                Three generations. One kitchen.
              </h2>
            </div>
            <p className="max-w-md text-sm text-[#3B1F1A]/70 leading-relaxed">
              Every proposal that leaves this kitchen has been read by one of them. Every event is on their calendar.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {FOUNDERS.map((f, i) => {
              const isJhanvi = f.name.toLowerCase().includes("jhanvi");
              const isAtul = f.name.toLowerCase().includes("atul");
              const isYogesh = f.name.toLowerCase().includes("yogesh");
              const photo = isJhanvi ? "/jhanvi.jpg" : isAtul ? "/atul.jpg" : null;
              return (
                <motion.article
                  key={f.name}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ delay: i * 0.1, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                  className={`bg-[#FDFBF6] border overflow-hidden ${
                    isYogesh ? "border-[#C9A961]/60" : "border-[#C9A961]/40"
                  }`}
                >
                  <div className="aspect-[4/5] relative overflow-hidden bg-[#F5C9B0]/40 flex items-center justify-center">
                    {photo ? (
                      <img
                        src={photo}
                        alt={`${f.name} — ${f.role} at Indian Alchemy × Platinum Hospitality`}
                        loading="lazy"
                        decoding="async"
                        className={`w-full h-full object-cover ${
                          isJhanvi ? "object-[35%_25%]" : "object-[center_20%]"
                        }`}
                      />
                    ) : (
                      <>
                        <div className="font-serif italic text-8xl text-[#C9A961]/50 select-none">
                          {f.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}
                        </div>
                        <div className="absolute inset-0 dot-grid opacity-25" />
                      </>
                    )}
                    {isYogesh && (
                      <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 h-6 px-2.5 rounded-full bg-[#3B1F1A]/80 text-[#C9A961] text-[9px] uppercase tracking-widest">
                        <Heart size={10} className="fill-[#C9A961]" strokeWidth={0} />
                        Legacy
                      </span>
                    )}
                  </div>
                  <div className="p-7 md:p-9">
                    <div className="text-[10px] uppercase tracking-[0.28em] text-[#C9A961]">
                      {f.role}
                    </div>
                    <div className="mt-3 font-serif text-2xl md:text-3xl text-[#3B1F1A] leading-tight">
                      {f.name}
                    </div>
                    <p className="mt-4 text-sm text-[#3B1F1A]/75 leading-relaxed">
                      {f.bio}
                    </p>
                  </div>
                </motion.article>
              );
            })}
          </div>
        </div>
      </section>

      {/* HERITAGE TIMELINE */}
      <section className="relative py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="mb-16">
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#C9A961]">
              Heritage
            </div>
            <h2 className="mt-6 font-serif font-light text-4xl md:text-5xl leading-[1.05] text-[#3B1F1A]">
              Twenty-five years, one straight line.
            </h2>
          </div>
          <div className="relative">
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-[#C9A961]/50" />
            <div className="space-y-12 md:space-y-16">
              {TIMELINE.map((item, i) => (
                <motion.div
                  key={item.year}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ delay: i * 0.05, duration: 0.7 }}
                  className={`relative pl-12 md:pl-0 md:grid md:grid-cols-2 md:gap-16 ${
                    i % 2 === 0 ? "" : "md:[&>*:first-child]:order-2"
                  }`}
                >
                  <div className={`${i % 2 === 0 ? "md:text-right md:pr-16" : "md:pl-16"}`}>
                    <div className="font-serif text-5xl md:text-6xl text-[#C9A961] font-light">
                      {item.year}
                    </div>
                  </div>
                  <div className={`mt-3 md:mt-4 ${i % 2 === 0 ? "md:pl-16" : "md:text-right md:pr-16"}`}>
                    <p className="text-[#3B1F1A]/80 leading-relaxed max-w-md md:max-w-none">
                      {item.body}
                    </p>
                  </div>
                  <span className="absolute left-[13px] md:left-1/2 top-3 -translate-x-1/2 w-3 h-3 rounded-full bg-[#C9A961] ring-4 ring-[#FDFBF6]" />
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section className="relative py-24 md:py-32 bg-[#F5C9B0]/20">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-14">
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="p-10 md:p-14 border border-[#C9A961]/40 peach-gradient">
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#C9A961]">Indian Alchemy</div>
            <div className="mt-4 font-serif text-3xl md:text-4xl text-[#3B1F1A]">Curated · Rooted · Elevated · Warm · Theatrical.</div>
          </motion.div>
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="p-10 md:p-14 border border-[#D42B2B]/25 bg-[#2B3A8C] text-[#FDFBF6]">
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#D42B2B]">Platinum Hospitality</div>
            <div className="mt-4 font-serif text-3xl md:text-4xl">Reliable · Scalable · Precise · Proven · Honest.</div>
          </motion.div>
        </div>
      </section>

      {/* PRESS — styled clippings with gold border */}
      <section className="relative py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="mb-16">
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#C9A961]">Press</div>
            <h2 className="mt-6 font-serif font-light text-4xl md:text-5xl leading-[1.05] text-[#3B1F1A]">
              What has been written.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
            {PRESS.map((p, i) => (
              <motion.figure
                key={p.outlet}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ delay: i * 0.1, duration: 0.9 }}
                className="group"
              >
                {/* Clipping mock — thin gold border, editorial layout */}
                <div className="relative aspect-[4/3] border border-[#C9A961]/70 bg-[#FDFBF6] p-8 md:p-10 overflow-hidden">
                  <div
                    aria-hidden
                    className="absolute inset-0 dot-grid opacity-30 pointer-events-none"
                  />
                  <div className="absolute inset-4 border border-[#C9A961]/25 pointer-events-none" />
                  <div className="relative">
                    <div className="flex items-center justify-between border-b border-[#3B1F1A]/25 pb-3">
                      <span className="font-serif italic text-lg text-[#3B1F1A]">
                        {p.outlet}
                      </span>
                      <span className="text-[10px] uppercase tracking-[0.28em] text-[#C9A961]">
                        {p.date}
                      </span>
                    </div>
                    <h3 className="mt-6 font-serif text-2xl md:text-3xl leading-tight text-[#3B1F1A]">
                      {i === 0
                        ? "A Delhi NCR icon, quietly plating the room."
                        : "A chip off the family business."}
                    </h3>
                    <p className="mt-4 text-sm text-[#3B1F1A]/70 leading-relaxed max-w-lg">
                      {p.note}
                    </p>
                    <div className="mt-6 flex items-center gap-3">
                      <IAWordmark height={22} />
                    </div>
                  </div>
                </div>
                <figcaption className="mt-4 flex items-center justify-between text-[11px] uppercase tracking-[0.24em] text-[#3B1F1A]/60">
                  <span>{p.outlet}</span>
                  <span>{p.date}</span>
                </figcaption>
                {p.href && (
                  <a
                    href={p.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-3 inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-[#3B1F1A] border-b border-[#C9A961] pb-0.5 hover:text-[#C9A961]"
                  >
                    Read article <ExternalLink size={12} />
                  </a>
                )}
              </motion.figure>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative bg-[#1A100D] text-[#FDFBF6] py-24 md:py-32 text-center">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <h2 className="font-serif font-light text-4xl md:text-6xl leading-[1.05]">
            Let&rsquo;s plate something <em className="italic text-[#C9A961]">unforgettable</em>.
          </h2>
          <Link
            to="/book"
            className="mt-10 inline-flex items-center gap-3 h-12 px-6 rounded-full bg-[#C9A961] text-[#3B1F1A] text-xs uppercase tracking-[0.28em] hover:bg-[#FDFBF6]"
          >
            Book a Consultation
          </Link>
        </div>
      </section>
    </div>
  );
}
